import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { LOCATIONS } from "@/content/locations";

const LOCATIONS_FAQS = [
  {
    q: "What's the difference between proximity and prominence in local SEO?",
    a: "Proximity is how close a searcher is to a business location; prominence is how authoritative and well-reviewed Google considers that business relative to competitors, based on signals like review volume, review recency, citation consistency, and link authority. Google's Local Pack algorithm blends both — a highly prominent business can rank for searchers several miles away, while a weakly-established one may only surface for people standing almost on top of it. Multi-location strategy in Texas is largely about deciding where to invest in prominence versus where sheer proximity will carry you.",
  },
  {
    q: "How does service-area SEO work for a business without a public storefront?",
    a: "Google allows service-area businesses to hide their exact address on their Google Business Profile while still defining a service radius or list of served cities. Ranking then depends more heavily on on-page geographic signals — city-specific service pages, structured data declaring the areas served, and reviews that mention job locations — since there's no physical foot traffic or storefront proximity to lean on. It's a workable model but requires more deliberate content architecture than a storefront business needs.",
  },
  {
    q: "Why treat Austin, San Antonio, Dallas, and Houston as separate strategies instead of one Texas-wide campaign?",
    a: "Because each metro has a different competitive density, different dominant local players, and often different buyer expectations. A service that's commoditised and price-driven in Houston's sprawling market might be a premium, relationship-driven purchase in Austin's smaller, more affluent core. Running one undifferentiated Texas campaign means either over-investing in a metro that doesn't need it or under-investing in one where competitors are dominating Local Pack results we haven't accounted for.",
  },
  {
    q: "What is the I-35 corridor dynamic between Austin and San Antonio?",
    a: "Interstate 35 links Austin and San Antonio through a fast-growing string of suburbs and satellite cities — New Braunfels, San Marcos, Kyle, Buda — where residential growth is outpacing the two anchor cities. Businesses serving that corridor often see meaningful search volume split across a handful of these smaller cities rather than concentrated in either metro, which means a corridor-aware SEO strategy needs city-specific pages for the mid-sized towns, not just the two big anchors, or it leaves volume on the table.",
  },
  {
    q: "How is DFW's local SEO landscape different from Austin's?",
    a: "Dallas-Fort Worth is really two adjacent, roughly equal-sized metros with distinct downtowns, competitive sets, and commuting patterns, plus a ring of large, independently significant suburbs like Plano and Frisco that behave almost like their own markets. A business targeting DFW broadly usually needs a Dallas-specific and a Fort Worth-specific presence at minimum, and often suburb-specific pages for Plano or Frisco, because a single \"Dallas\" listing won't carry proximity weight for a Frisco searcher thirty miles north.",
  },
  {
    q: "Does having multiple location pages risk being seen as doorway pages by Google?",
    a: "Only if the pages are templated with nothing but a swapped city name. Google's guidelines specifically penalise low-value pages created purely to rank for geographic variants without offering distinct, useful content. Legitimate multi-location pages need real differentiation — local landmarks, metro-specific service considerations, local proof or testimonials, and genuinely different content depth — which is the standard we hold our own location pages to and recommend any client building multiple city pages follow.",
  },
  {
    q: "Should a growing Texas business build every city page at once, or expand gradually?",
    a: "Gradually, tied to actual demand or expansion. Building ten thin city pages simultaneously dilutes the effort available for each and increases the doorway-page risk described above. We generally recommend proving out one or two well-built location pages, confirming they earn genuine rankings and traffic, and using that as the template quality bar before expanding to the next metro.",
  },
];


export const Route = createFileRoute("/locations/")({
  head: () => ({ ...pageMeta({
    path: "/locations/",
    title: "Texas SEO Agency — Austin, Dallas & Houston",
    description:
      "Optimize Plus serves 11 Texas markets from Austin — Dallas, Houston, San Antonio, Fort Worth, Plano, Frisco and more. Per-metro local SEO programs.",
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations/" }]),
    faqLd(LOCATIONS_FAQS),
  ) }),
  component: LocationsIndex,
});


function LocationsIndex() {
  const byRegion = LOCATIONS.reduce(
    (acc, l) => {
      (acc[l.region] ||= []).push(l);
      return acc;
    },
    {} as Record<string, typeof LOCATIONS>,
  );
  const regionOrder = ["Central Texas", "North Texas", "Gulf Coast", "South Texas"];
  const sortedRegions = Object.keys(byRegion).sort(
    (a, b) => regionOrder.indexOf(a) - regionOrder.indexOf(b),
  );

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-20 md:pt-28 pb-10">
          <EyebrowLabel>Locations · 11 Texas markets</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[1.02] max-w-4xl text-balance">
            Texas, treated as
            <span className="text-gradient"> eleven different markets.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Buyers in Fort Worth don't search like buyers in Austin. Local Pack in
            Frisco behaves nothing like Local Pack in San Antonio. We build
            programmes tuned to each metro, not one flattened Texas playbook.
          </p>
        </section>

        <div className="container-page pb-24 space-y-16">
          {sortedRegions.map((region) => (
            <section key={region}>
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-display text-2xl md:text-3xl">{region}</h2>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {byRegion[region].length} markets
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {byRegion[region].map((l) => (
                  <Link
                    key={l.slug}
                    to={"/seo-agency-{$slug}" as any}
                    params={{ slug: l.slug } as any}
                    className="surface-card p-6 hover:-translate-y-0.5 transition-transform flex flex-col group"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {l.eyebrow}
                    </div>
                    <div className="mt-2 font-display text-2xl group-hover:text-primary transition-colors">{l.city}, TX</div>
                    <p className="mt-3 text-sm text-muted-foreground line-clamp-3 flex-1">
                      {l.hero.deck}
                    </p>
                    <div className="mt-4 text-sm text-primary font-semibold flex items-center gap-1">
                      Explore Market <span aria-hidden>→</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section className="container-page pb-24">
          <div className="surface-card p-8 md:p-10">
            <EyebrowLabel>Not in Texas?</EyebrowLabel>
            <h2 className="mt-3 font-display text-3xl md:text-4xl">
              We run national programmes from Texas.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              Most of our roster is outside Texas. The location system exists
              because Texas is where we're headquartered and where in-person
              strategy days are practical. National and international clients get
              the same operational discipline, remote-first.
            </p>
            <div className="mt-6">
              <Link
                to="/contact/"
                search={{ location: "", service: "" }}
                className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
              >
                Start a conversation →
              </Link>
            </div>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>How it works</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            How service-area SEO works across Texas metros
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Local search results are assembled from a mix of signals Google groups broadly into
              relevance, distance, and prominence. Relevance is whether your business profile and website
              content actually match the query. Distance is the searcher's location relative to yours, or
              relative to the service area you've declared. Prominence is a composite of reviews, links,
              citations, and general web authority. A multi-metro business has to manage all three
              independently for every market it competes in — a strong Austin Google Business Profile
              does not transfer any ranking benefit to a Dallas search, because the two are evaluated
              entirely separately.
            </p>
            <p>
              For businesses with a physical location in each metro, this mostly means a well-optimised,
              consistently categorised Google Business Profile per location, paired with locally relevant
              on-site content and a citation profile with matching name, address, and phone details across
              directories. For service-area businesses covering a metro without a storefront in every
              city, the on-site content — dedicated pages per served city with genuinely local detail — has
              to work harder, since there's no physical proximity signal in that city to lean on.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>Proximity vs prominence</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Proximity vs prominence, and what it means for strategy
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              These two forces trade off against each other in ways that shape where we spend budget. A
              business with weak prominence — few reviews, thin citation coverage — will only surface for
              searchers very close by, no matter how good its website is. A business with strong
              prominence can pull rankings from several miles away, effectively expanding its practical
              service radius without opening a second location. For a client deciding whether to open a
              physical presence in a new metro or simply build authority to reach it remotely, this
              distinction is often the deciding factor.
            </p>
            <p>
              In competitive urban cores like downtown Austin or downtown Dallas, proximity variance is
              small — most competitors are within a few miles of each other — so prominence usually decides
              rankings. In sprawling metros like Houston, where meaningful distances separate
              neighbourhoods, proximity carries more weight, and a business without a location on the
              correct side of town may struggle to rank there regardless of review count.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>Multi-location strategy</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Building a multi-location strategy that doesn't dilute itself
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              The most common mistake we see in multi-location Texas SEO is treating every additional city
              page as free upside with no real cost. In practice, each new location page needs enough
              genuinely distinct content — local proof points, metro-specific service considerations,
              locally relevant imagery and testimonials — to avoid reading as a template with the city name
              swapped, and producing that content takes real time. Spreading that effort across too many
              cities at once usually means every page ends up thin, which risks the exact doorway-page
              problem Google's guidelines warn against.
            </p>
            <p>
              We generally sequence multi-location rollouts by expected demand: build and prove the
              highest-opportunity metro first, confirm the page earns genuine rankings and converts
              traffic, then use that as the quality bar for the next market rather than launching everything
              simultaneously. It's a slower rollout but avoids diluting a business's overall authority
              across a dozen half-built pages.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>Corridor dynamics</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Corridor dynamics: I-35, DFW, and Houston
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              The Austin–San Antonio corridor along I-35 is one of the fastest-growing residential
              stretches in the country, and much of that growth lands in mid-sized cities between the two
              anchors — New Braunfels, San Marcos, Kyle, Buda — rather than in either downtown. A business
              serving this corridor that only builds Austin and San Antonio pages is leaving meaningful,
              growing search volume unaddressed in the cities in between.
            </p>
            <p>
              DFW behaves differently: it's genuinely two peer metros, Dallas and Fort Worth, each with its
              own downtown, media market, and competitive landscape, ringed by large, semi-independent
              suburbs like Plano and Frisco that often have enough population and commercial activity to
              justify their own dedicated page rather than being folded into a generic "Dallas area"
              listing.
            </p>
            <p>
              Houston's dynamic is shaped more by sheer geographic sprawl than by distinct sub-metros — the
              city and its surrounding communities like The Woodlands cover enormous distances, which means
              proximity weighs more heavily in Houston's Local Pack results than it does in Austin's
              comparatively compact core, and a business's exact location relative to the neighbourhoods it
              wants to reach matters more here than almost anywhere else in the state.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Frequently asked questions
          </h2>
          <dl className="mt-8 space-y-6">
            {LOCATIONS_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
